import { useState, useEffect } from "react";
import { Button } from "../../../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../components/ui/select";
import { Plus, X } from "lucide-react";

export type CanFieldFormData = {
    can_id: string;
    start_byte: string;
    byte_count: string;
    start_bit: string;
    bit_count: string;
    frequency: string;
    update_frequency: string;
    criticalStates: { value: string; operator: string }[];
};

const defaultFormData: CanFieldFormData = {
    can_id: "",
    start_byte: "0",
    byte_count: "1",
    start_bit: "0",
    bit_count: "0",
    frequency: "0",
    update_frequency: "0",
    criticalStates: [],
};

const operatorLabels: Record<string, string> = {
    eq: "= (Equal)",
    ne: "!= (Not Equal)",
    gt: "> (Greater Than)",
    lt: "< (Less Than)",
    ge: ">= (Greater or Equal)",
    le: "<= (Less or Equal)",
};

type CanConfigDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (data: CanFieldFormData) => void;
    initialData?: any;
    isPending: boolean;
    attributeName: string;
};

export const CanConfigDialog = ({ open, onOpenChange, onSave, initialData, isPending, attributeName }: CanConfigDialogProps) => {
    const [form, setForm] = useState<CanFieldFormData>(defaultFormData);
    const isEditing = !!initialData;

    useEffect(() => {
        if (open) {
            if (initialData) {
                setForm({
                    can_id: initialData.can_id || "",
                    start_byte: String(initialData.start_byte ?? 0),
                    byte_count: String(initialData.byte_count ?? 1),
                    start_bit: String(initialData.start_bit ?? 0),
                    bit_count: String(initialData.bit_count ?? 0),
                    frequency: String(initialData.frequency ?? 0),
                    update_frequency: String(initialData.update_frequency ?? 0),
                    criticalStates: (initialData.criticalStates || []).map((cs: any) => ({
                        value: String(cs.value),
                        operator: cs.operator,
                    })),
                });
            } else {
                setForm(defaultFormData);
            }
        }
    }, [open, initialData]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(form);
    };

    const addCriticalState = () => {
        setForm((prev) => ({
            ...prev,
            criticalStates: [...prev.criticalStates, { value: "0", operator: "ge" }],
        }));
    };

    const removeCriticalState = (index: number) => {
        setForm((prev) => ({
            ...prev,
            criticalStates: prev.criticalStates.filter((_, i) => i !== index),
        }));
    };

    const updateCriticalState = (index: number, field: "value" | "operator", value: string) => {
        setForm((prev) => ({
            ...prev,
            criticalStates: prev.criticalStates.map((cs, i) => (i === index ? { ...cs, [field]: value } : cs)),
        }));
    };

    const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring";
    const labelClass = "text-xs font-medium text-muted-foreground uppercase tracking-wide";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? "Edit" : "Configure"} CAN Field
                        <span className="text-muted-foreground font-normal text-sm ml-2">({attributeName})</span>
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>CAN ID *</label>
                            <input
                                value={form.can_id}
                                onChange={(e) => setForm((prev) => ({ ...prev, can_id: e.target.value }))}
                                required
                                placeholder="e.g. 0x100"
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Start Byte *</label>
                            <input
                                type="number"
                                min="0"
                                max="7"
                                value={form.start_byte}
                                onChange={(e) => setForm((prev) => ({ ...prev, start_byte: e.target.value }))}
                                required
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Byte Count</label>
                            <input
                                type="number"
                                min="1"
                                max="8"
                                value={form.byte_count}
                                onChange={(e) => setForm((prev) => ({ ...prev, byte_count: e.target.value }))}
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Start Bit</label>
                            <input
                                type="number"
                                min="0"
                                max="7"
                                value={form.start_bit}
                                onChange={(e) => setForm((prev) => ({ ...prev, start_bit: e.target.value }))}
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Bit Count</label>
                            <input
                                type="number"
                                min="0"
                                value={form.bit_count}
                                onChange={(e) => setForm((prev) => ({ ...prev, bit_count: e.target.value }))}
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Frequency (ms)</label>
                            <input
                                type="number"
                                min="0"
                                value={form.frequency}
                                onChange={(e) => setForm((prev) => ({ ...prev, frequency: e.target.value }))}
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Update Frequency (ms)</label>
                            <input
                                type="number"
                                min="0"
                                value={form.update_frequency}
                                onChange={(e) => setForm((prev) => ({ ...prev, update_frequency: e.target.value }))}
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between">
                            <label className={labelClass}>Critical States</label>
                            <button
                                type="button"
                                onClick={addCriticalState}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                Add Rule
                            </button>
                        </div>

                        {form.criticalStates.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No critical state rules defined.</p>
                        ) : (
                            <div className="space-y-2">
                                {form.criticalStates.map((cs, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <Select value={cs.operator} onValueChange={(v) => updateCriticalState(index, "operator", v)}>
                                            <SelectTrigger className="h-9 w-[180px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(operatorLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>
                                                        {label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <input
                                            type="number"
                                            value={cs.value}
                                            onChange={(e) => updateCriticalState(index, "value", e.target.value)}
                                            placeholder="Value"
                                            className={`${inputClass} flex-1`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeCriticalState(index)}
                                            className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending ? "Saving..." : "Save Configuration"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
